import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Users, Mail, Activity, Shield, AlertTriangle, Clock,
  TrendingUp, Server, Database, CheckCircle, XCircle,
  ArrowLeft, RefreshCw, UserPlus, MessageSquare, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [signups, setSignups] = useState(null);
  const [activity, setActivity] = useState(null);
  const [emails, setEmails] = useState(null);
  const [health, setHealth] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [summaryRes, signupsRes, activityRes, emailsRes, healthRes, complianceRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/monitor/summary`, { headers }),
        fetch(`${API_URL}/api/admin/monitor/signups/realtime?hours=24`, { headers }),
        fetch(`${API_URL}/api/admin/monitor/activity?hours=24`, { headers }),
        fetch(`${API_URL}/api/admin/monitor/emails`, { headers }),
        fetch(`${API_URL}/api/admin/monitor/health`, { headers }),
        fetch(`${API_URL}/api/admin/monitor/compliance`, { headers })
      ]);

      if (summaryRes.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (signupsRes.ok) setSignups(await signupsRes.json());
      if (activityRes.ok) setActivity(await activityRes.json());
      if (emailsRes.ok) setEmails(await emailsRes.json());
      if (healthRes.ok) setHealth(await healthRes.json());
      if (complianceRes.ok) setCompliance(await complianceRes.json());

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Monitoring Dashboard</h1>
              <p className="text-gray-400 text-sm">
                Real-time platform metrics
                {lastRefresh && (
                  <span className="ml-2">
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="key-metrics-section">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Real Users</p>
                    <p className="text-3xl font-bold text-white">{summary.key_metrics?.real_users_total || 0}</p>
                  </div>
                  <Users className="h-10 w-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Signups Today</p>
                    <p className="text-3xl font-bold text-green-400">{summary.key_metrics?.signups_today || 0}</p>
                  </div>
                  <UserPlus className="h-10 w-10 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Posts Today</p>
                    <p className="text-3xl font-bold text-blue-400">{summary.key_metrics?.posts_today || 0}</p>
                  </div>
                  <MessageSquare className="h-10 w-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Active Now</p>
                    <p className="text-3xl font-bold text-amber-400">{summary.key_metrics?.active_now || 0}</p>
                  </div>
                  <Activity className="h-10 w-10 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Flags */}
        {summary?.status_flags && (
          <div className="flex gap-3 flex-wrap">
            <Badge 
              variant={summary.status_flags.has_real_users ? "default" : "secondary"}
              className={summary.status_flags.has_real_users ? "bg-green-600" : "bg-yellow-600"}
            >
              {summary.status_flags.has_real_users ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
              {summary.status_flags.has_real_users ? "Real Users Active" : "Awaiting Real Users"}
            </Badge>
            <Badge 
              variant={summary.status_flags.sendgrid_configured ? "default" : "destructive"}
              className={summary.status_flags.sendgrid_configured ? "bg-green-600" : "bg-red-600"}
            >
              {summary.status_flags.sendgrid_configured ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
              {summary.status_flags.sendgrid_configured ? "SendGrid Active" : "SendGrid Not Configured"}
            </Badge>
            <Badge 
              variant="default"
              className={summary.status_flags.platform_active ? "bg-green-600" : "bg-gray-600"}
            >
              {summary.status_flags.platform_active ? "Platform Active" : "Low Activity"}
            </Badge>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Signups */}
          <Card className="bg-slate-800/50 border-slate-700" data-testid="recent-signups-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-green-400" />
                Recent Signups
              </CardTitle>
              <CardDescription className="text-gray-400">
                Last 24 hours: {signups?.totals?.real_users || 0} real users
              </CardDescription>
            </CardHeader>
            <CardContent>
              {signups?.recent_signups?.length > 0 ? (
                <div className="space-y-3">
                  {signups.recent_signups.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {user.name?.[0] || user.username?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || user.username}</p>
                          <p className="text-gray-400 text-sm">@{user.username}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-gray-300 border-gray-600">
                          {user.subscription_tier || 'basic'}
                        </Badge>
                        <p className="text-gray-500 text-xs mt-1">
                          {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No recent signups</p>
                  <p className="text-sm">Real users will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Platform Activity */}
          <Card className="bg-slate-800/50 border-slate-700" data-testid="activity-metrics-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-400" />
                Platform Activity
              </CardTitle>
              <CardDescription className="text-gray-400">
                Last 24 hours engagement metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activity?.metrics ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-sm">New Posts</p>
                      <p className="text-2xl font-bold text-white">{activity.metrics.new_posts}</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Likes</p>
                      <p className="text-2xl font-bold text-red-400">{activity.metrics.likes}</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Comments</p>
                      <p className="text-2xl font-bold text-blue-400">{activity.metrics.comments}</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Messages</p>
                      <p className="text-2xl font-bold text-green-400">{activity.metrics.messages}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Engagement Rate</span>
                      <span className="text-2xl font-bold text-purple-300">{activity.engagement_rate}x</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No activity data</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Status */}
          <Card className="bg-slate-800/50 border-slate-700" data-testid="email-status-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="h-5 w-5 text-amber-400" />
                Email Service Status
              </CardTitle>
              <CardDescription className="text-gray-400">
                {emails?.sendgrid_configured ? 'SendGrid Active' : 'Mock Mode (Emails Logged)'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {emails?.stats ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${emails.sendgrid_configured ? 'bg-green-900/30 border border-green-700' : 'bg-yellow-900/30 border border-yellow-700'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {emails.sendgrid_configured ? (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      )}
                      <span className={emails.sendgrid_configured ? 'text-green-300' : 'text-yellow-300'}>
                        {emails.sendgrid_configured ? 'Production Ready' : 'Add SENDGRID_API_KEY to enable'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-2xl font-bold text-white">{emails.stats.total_emails}</p>
                      <p className="text-gray-400 text-sm">Total</p>
                    </div>
                    <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-2xl font-bold text-green-400">{emails.stats.sent_via_sendgrid}</p>
                      <p className="text-gray-400 text-sm">Sent</p>
                    </div>
                    <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-400">{emails.stats.mock_logged}</p>
                      <p className="text-gray-400 text-sm">Mocked</p>
                    </div>
                  </div>
                  {emails.stats.by_template_type && Object.keys(emails.stats.by_template_type).length > 0 && (
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-gray-400 text-sm mb-2">By Type:</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(emails.stats.by_template_type).map(([type, count]) => (
                          <Badge key={type} variant="outline" className="text-gray-300 border-gray-600">
                            {type}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No email data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* System Health */}
          <Card className="bg-slate-800/50 border-slate-700" data-testid="system-health-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Server className="h-5 w-5 text-green-400" />
                System Health
              </CardTitle>
              <CardDescription className="text-gray-400">
                Infrastructure status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {health ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${health.status === 'healthy' ? 'bg-green-900/30 border border-green-700' : 'bg-yellow-900/30 border border-yellow-700'}`}>
                    <div className="flex items-center gap-2">
                      {health.status === 'healthy' ? (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      )}
                      <span className={health.status === 'healthy' ? 'text-green-300 font-medium' : 'text-yellow-300 font-medium'}>
                        {health.status === 'healthy' ? 'All Systems Operational' : 'Warning: Check Issues'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                      <span className="text-gray-300 flex items-center gap-2">
                        <Database className="h-4 w-4" /> Database
                      </span>
                      <Badge className={health.database === 'healthy' ? 'bg-green-600' : 'bg-red-600'}>
                        {health.database}
                      </Badge>
                    </div>
                    {health.collection_sizes && Object.entries(health.collection_sizes).map(([name, count]) => (
                      <div key={name} className="flex items-center justify-between p-2 bg-slate-700/30 rounded text-sm">
                        <span className="text-gray-400">{name}</span>
                        <span className="text-gray-300">{count.toLocaleString()} docs</span>
                      </div>
                    ))}
                  </div>

                  {health.last_24h && (
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-700">
                      <div className="text-center p-2 bg-slate-700/50 rounded">
                        <p className={`text-xl font-bold ${health.last_24h.errors === 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {health.last_24h.errors}
                        </p>
                        <p className="text-gray-400 text-sm">Errors (24h)</p>
                      </div>
                      <div className="text-center p-2 bg-slate-700/50 rounded">
                        <p className="text-xl font-bold text-amber-400">{health.last_24h.security_events}</p>
                        <p className="text-gray-400 text-sm">Security Events</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Server className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Loading health data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Compliance Section */}
        {compliance && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="compliance-status-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-400" />
                Compliance Status
              </CardTitle>
              <CardDescription className="text-gray-400">
                GDPR, CCPA, and regulatory compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{compliance.pending_deletion_requests}</p>
                  <p className="text-gray-400 text-sm">Pending Deletions</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{compliance.total_data_subject_requests}</p>
                  <p className="text-gray-400 text-sm">DSR Requests</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{compliance.consent_stats?.analytics_opted_in || 0}</p>
                  <p className="text-gray-400 text-sm">Analytics Opt-In</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                  <p className={`text-2xl font-bold ${compliance.open_breach_reports === 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {compliance.open_breach_reports}
                  </p>
                  <p className="text-gray-400 text-sm">Open Breaches</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Compliance Score</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        style={{ width: `${compliance.compliance_score}%` }}
                      />
                    </div>
                    <span className="text-xl font-bold text-purple-300">{compliance.compliance_score}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/admin">
            <Button variant="outline" className="border-purple-600 text-purple-300 hover:bg-purple-900/30">
              Full Admin Panel
            </Button>
          </Link>
          <Link to="/settings">
            <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800/30">
              Settings
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
